import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Inbox, AlertTriangle, Search, X, ChevronDown } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTickets, useTicketCompanies, useTicketCount } from '@/queries/tickets'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { widerThanShell } from '@/lib/layout'
import { TicketRow } from './TicketRow'
import { Pager, TICKET_PAGE_SIZE } from './Pager'
import { displayStatusLabels, priorityLabels } from '@/lib/status'
import type { DisplayStatus, Ticket, TicketPriority } from '@/types/ticket'

// Derived statuses the queue can filter on (the API's derivedStatus). Awaiting
// User also covers a tracked ticket's pending: its proposal to close, waiting on the client.
const STATUSES: Array<{ value: string; label: string }> = (
  ['open', 'user_reply', 'in_progress', 'awaiting_user', 'on_hold', 'resolved', 'closed'] as DisplayStatus[]
).map(s => ({ value: s === 'awaiting_user' ? 'awaiting_user,pending' : s, label: displayStatusLabels[s] }))

// What the queue shows when the URL carries no status: open, user reply and
// in progress tickets, the ones waiting on us. Resolved via the API's
// `needs_attention` status, so pagination and the total both see the same set.
export const DEFAULT_QUEUE_STATUS = 'needs_attention'
const NEEDS_ATTENTION_STATUSES = ['open', 'user_reply', 'in_progress']
const PRIORITIES: TicketPriority[] = ['high', 'normal', 'low']

// Tabs are presets over the status and assignee filters.
const VIEWS: Array<{ label: string; status?: string; assignee?: string }> = [
  { label: 'Needs attention' },
  { label: 'Mine', assignee: 'me' },
  { label: 'All', status: 'all' },
]

function selectedStatuses(status: string): string[] {
  if (status === 'all') return []
  return status === DEFAULT_QUEUE_STATUS ? NEEDS_ATTENTION_STATUSES : status.split(',')
}

// Writes the checked statuses back as the URL's status: none checked is All, the needs-attention set is the default.
function statusParam(values: string[]): string | undefined {
  const flat = values.flatMap(v => v.split(','))
  if (!flat.length) return 'all'
  const isDefault = flat.length === NEEDS_ATTENTION_STATUSES.length && NEEDS_ATTENTION_STATUSES.every(s => flat.includes(s))
  return isDefault ? undefined : flat.join(',')
}

const SORT_OPTIONS = [
  { value: '-lastMessageAt', label: 'Latest activity' },
  { value: '-createdAt', label: 'Newest' },
  { value: 'createdAt', label: 'Oldest' },
  { value: 'priority', label: 'Priority' },
]

/** Queue filters — mirrored into the /admin URL search params by the route. */
export interface QueueFilters {
  status?: string
  priority?: string
  assignee?: string
  companyId?: string
  q?: string
  sort?: string
  page?: number
}

interface AdminUser {
  _id: string
  name?: string
  email?: string
}

// V2 filterbar select (mockup `.filterbar select`): pill radius, 36px,
// hairline border — shared by every select in this filterbar.
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// V2 table header cell (mockup `th`): 36px, 11px uppercase letterspaced label.
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// V2 table card. overflow-x-auto rather than overflow-hidden: still clips to
// the rounded corners, but a narrow viewport scrolls a wide table instead of
// silently losing its right-hand columns.
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

// Eight columns and six filters don't fit AppShell's 1080px column, so the queue takes up to 1400px like the tracker.
const WIDE_PAGE = widerThanShell(1400, '4rem')

export function AdminQueueView({
  filters,
  onChange,
}: {
  filters: QueueFilters
  onChange: (next: QueueFilters) => void
}) {
  // Any filter change starts again from the first page.
  const set = (patch: Partial<QueueFilters>) => onChange({ ...filters, page: undefined, ...patch })

  // Debounced search: local input state, push to the URL after a pause.
  const [searchText, setSearchText] = useState(filters.q ?? '')
  useEffect(() => { setSearchText(filters.q ?? '') }, [filters.q])
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchText.trim()
      if ((filters.q ?? '') !== trimmed) set({ q: trimmed || undefined })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const status = filters.status ?? DEFAULT_QUEUE_STATUS
  const page = filters.page ?? 1
  const params: Record<string, unknown> = { limit: TICKET_PAGE_SIZE }
  if (status !== 'all') params.status = status
  if (filters.priority) params.priority = filters.priority
  if (filters.assignee) params.assignedTo = filters.assignee
  if (filters.companyId) params.companyId = filters.companyId
  if (filters.q) params.q = filters.q
  if (filters.sort) params.sort = filters.sort
  if (page > 1) params.skip = (page - 1) * TICKET_PAGE_SIZE

  const { data, isLoading, error, refetch } = useTickets(params)
  const viewCounts = [
    useTicketCount({ status: DEFAULT_QUEUE_STATUS }),
    useTicketCount({ status: DEFAULT_QUEUE_STATUS, assignedTo: 'me' }),
    useTicketCount({}),
  ]
  const activeView = VIEWS.find(v => v.status === filters.status && v.assignee === filters.assignee)
  const checked = selectedStatuses(status)
  const isChecked = (value: string) => value.split(',').every(s => checked.includes(s))
  const toggleStatus = (value: string) =>
    set({ status: statusParam(isChecked(value) ? checked.filter(s => !value.split(',').includes(s)) : [...checked, value]) })
  const statusSummary =
    status === 'all' ? 'All statuses' : STATUSES.filter(s => isChecked(s.value)).map(s => s.label).join(', ') || 'All statuses'
  const companies = useTicketCompanies()
  const admins = useQuery({
    queryKey: ['support-tickets', 'admins'],
    queryFn: () => api<{ data: AdminUser[] }>('/support-tickets/admins'),
    staleTime: 5 * 60_000,
  })

  // Rows carry only companyId — no companyName join server-side — so the
  // Company column's display name is joined client-side from the same
  // company list the filter select uses (same pattern as TrackerListView's
  // admin variant).
  const companyNames = new Map((companies.data?.data ?? []).map(c => [c._id, c.name ?? c._id]))

  const hasFilters = Boolean(filters.priority || filters.companyId || filters.q || !activeView)
  const countLabel = data
    ? status === DEFAULT_QUEUE_STATUS && !filters.assignee
      ? `${data.total} ticket${data.total === 1 ? ' needs' : 's need'} attention`
      : `${data.total} ticket${data.total === 1 ? '' : 's'}`
    : ''
  const trackedTotal = data?.trackedTotal ?? 0

  return (
    <div className="flex-1 min-w-0" style={WIDE_PAGE}>
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-lg font-semibold text-etn-text">Queue</h1>
        <span className="text-etn-micro text-etn-text-soft tabular-nums">
          {countLabel}
        </span>
        {hasFilters && (
          <button
            type="button"
            onClick={() => onChange({ sort: filters.sort, status: activeView?.status, assignee: activeView?.assignee })}
            className="inline-flex items-center gap-1 text-etn-micro text-etn-text-soft hover:text-etn-text"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      <Tabs
        className="mb-4"
        value={activeView?.label ?? ''}
        onValueChange={label => {
          const view = VIEWS.find(v => v.label === label)
          if (view) set({ status: view.status, assignee: view.assignee })
        }}
      >
        <TabsList aria-label="Queue views">
          {VIEWS.map((view, index) => (
            <TabsTrigger key={view.label} value={view.label}>
              {view.label}
              {viewCounts[index].data !== undefined && (
                <span className="tabular-nums text-etn-micro font-normal text-etn-text-soft">{viewCounts[index].data}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[200px] max-w-[320px] flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
          <Input
            pill
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search subject, name or email…"
            aria-label="Search queue"
            className="pl-9"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Status" className={`${filterSelectClass} inline-flex max-w-56 items-center gap-2`}>
              <span className="truncate">{statusSummary}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-etn-text-soft" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1.5">
            {STATUSES.map(s => (
              <label key={s.value} className="flex cursor-pointer items-center gap-2.5 rounded-etn-menu px-2.5 py-1.5 text-etn-control hover:bg-etn-control-subtle">
                <input type="checkbox" checked={isChecked(s.value)} onChange={() => toggleStatus(s.value)} />
                {s.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
        <select
          aria-label="Priority"
          className={filterSelectClass}
          value={filters.priority ?? 'all'}
          onChange={e => set({ priority: e.target.value === 'all' ? undefined : e.target.value })}
        >
          <option value="all">All priorities</option>
          {PRIORITIES.map(p => (
            <option key={p} value={p}>{priorityLabels[p]}</option>
          ))}
        </select>
        <select
          aria-label="Assignee"
          className={filterSelectClass}
          value={filters.assignee ?? ''}
          onChange={e => set({ assignee: e.target.value || undefined })}
        >
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="none">Unassigned</option>
          {admins.data?.data.map(a => (
            <option key={a._id} value={a._id}>{a.name ?? a.email ?? a._id}</option>
          ))}
        </select>
        <select
          aria-label="Company"
          className={`${filterSelectClass} max-w-44`}
          value={filters.companyId ?? ''}
          onChange={e => set({ companyId: e.target.value || undefined })}
        >
          <option value="">All companies</option>
          {companies.data?.data.map(c => (
            <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
          ))}
        </select>
        <select
          aria-label="Sort"
          className={filterSelectClass}
          value={filters.sort ?? '-lastMessageAt'}
          onChange={e => set({ sort: e.target.value === '-lastMessageAt' ? undefined : e.target.value })}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <ListSkeleton rows={5} />}

      {error && (
        <div className="flex flex-col items-center gap-3 py-16">
          <EmptyState
            icon={<AlertTriangle />}
            title="Couldn't load the queue."
            body="Something went wrong. Please try again."
          />
          <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      {!isLoading && !error && (data?.data.length ?? 0) === 0 && (
        trackedTotal > 0
          ? (
            <EmptyState
              icon={<Inbox />}
              title="No tickets match."
              body={`${trackedTotal} matching item${trackedTotal === 1 ? ' is' : 's are'} on the Client Tracker.`}
              cta={{ label: 'Open the Client Tracker', to: '/tracker', search: filters.companyId ? { company: filters.companyId } : undefined }}
            />
          )
          : hasFilters
            ? <EmptyState icon={<Inbox />} title="No tickets match." body="Try widening the filters." />
            : activeView?.assignee === 'me'
              ? <EmptyState icon={<Inbox />} title="You're all caught up." body="Nothing assigned to you needs attention." />
              : <EmptyState icon={<Inbox />} title="Queue is clear." body="No tickets need triage right now." />
      )}

      {!isLoading && !error && (data?.data.length ?? 0) > 0 && (
        <div className={tableCardClass}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th scope="col" className={thClass}>Ticket</th>
                <th scope="col" className={thClass}>From</th>
                <th scope="col" className={thClass}>Company</th>
                <th scope="col" className={thClass}>Assigned to</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={thClass}>Priority</th>
                <th scope="col" className={thClass}>SLA</th>
                <th scope="col" className={thClass}>Time to complete</th>
                <th scope="col" className={thClass}>Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-etn-border-row">
              {data!.data.map((t: Ticket) => (
                <TicketRow
                  key={t._id}
                  ticket={t}
                  relation={t.viewerRelation ?? 'admin'}
                  hrefBase="/admin/tickets"
                  companyName={companyNames.get(t.companyId) ?? 'Unknown company'}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !error && data && (
        <Pager page={page} total={data.total} onPage={next => onChange({ ...filters, page: next > 1 ? next : undefined })} />
      )}
    </div>
  )
}
